import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { hasAnalyticsConsent } from 'dashboard/helper/AnalyticsHelper/utils/trackingEnvelope';
import { createLogger } from 'shared/helpers/logger';

const logger = createLogger('riskAssessment');

const safeWindow = win => {
  if (win) return win;
  if (typeof window === 'undefined') return null;
  return window;
};

const currentAccountForUser = user => {
  const accounts = Array.isArray(user?.accounts) ? user.accounts : [];
  return accounts.find(account => account.id === user.account_id) || accounts[0];
};

export const accountFeatureEnabled = (
  user,
  flag = FEATURE_FLAGS.INCOGNIA_RISK
) => Boolean(currentAccountForUser(user)?.features?.[flag]);

export const stableRiskAccountId = user =>
  user?.pubsub_token ||
  user?.uuid ||
  user?.identifier_uuid ||
  user?.custom_attributes?.uuid ||
  null;

export const incogniaApplicationId = win =>
  safeWindow(win)?.thirdPartySdkConfig?.incogniaApplicationId ||
  safeWindow(win)?.globalConfig?.INCOGNIA_APPLICATION_ID ||
  '';

export const incogniaSdk = win =>
  safeWindow(win)?.IncogniaWebSdk || safeWindow(win)?.incognia || null;

export const shouldRunRiskAssessment = ({
  featureEnabled = false,
  consent = hasAnalyticsConsent(),
  appId,
  sdk,
} = {}) => Boolean(featureEnabled && consent && appId && sdk);

export const initRiskAssessment = async ({
  featureEnabled = false,
  consent = hasAnalyticsConsent(),
  appId = incogniaApplicationId(),
  sdk = incogniaSdk(),
} = {}) => {
  if (!shouldRunRiskAssessment({ featureEnabled, consent, appId, sdk })) {
    return false;
  }

  try {
    await sdk.init(appId);
    return true;
  } catch (error) {
    logger.error(error, { action: 'incognia_init' });
    return false;
  }
};

export const setRiskAssessmentAccountId = async ({
  user,
  featureEnabled = accountFeatureEnabled(user),
  consent = hasAnalyticsConsent(),
  sdk = incogniaSdk(),
} = {}) => {
  const accountId = stableRiskAccountId(user);
  if (!featureEnabled || !consent || !sdk?.setAccountId || !accountId) {
    return false;
  }

  try {
    await sdk.setAccountId(accountId);
    return true;
  } catch (error) {
    logger.error(error, { action: 'incognia_set_account_id' });
    return false;
  }
};

export const clearRiskAssessmentAccountId = async ({ sdk = incogniaSdk() } = {}) => {
  if (!sdk?.clearAccountId) return false;

  try {
    await sdk.clearAccountId();
    return true;
  } catch (error) {
    logger.error(error, { action: 'incognia_clear_account_id' });
    return false;
  }
};

export const initRiskAssessmentForUser = async user => {
  const featureEnabled = accountFeatureEnabled(user);
  const initialized = await initRiskAssessment({ featureEnabled });
  if (!initialized) return false;
  return setRiskAssessmentAccountId({ user, featureEnabled });
};
